import PropTypes from 'prop-types';
import { flexRender } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';

import { cn } from '#/lib/utils';
import { isColumnOrderLocked } from '#/hooks/table/mergeColumnOrder';
import ColumnFilterPopover from '#/modules/commonComponents/table/ColumnFilterPopover';
import ColumnFilterTextInput from '#/modules/commonComponents/table/ColumnFilterTextInput';
import ColumnFilterDateInput from '#/modules/commonComponents/table/ColumnFilterDateInput';
import ColumnFilterQtyPopover from '#/modules/commonComponents/table/ColumnFilterQtyPopover';

function getColumnMinWidth(column) {
  return column.columnDef.minSize ?? column.getSize();
}

function renderSortIndicator(isSorted) {
  if (isSorted === 'asc') {
    return <ChevronUp size={14} className="text-primary-600" />;
  }
  if (isSorted === 'desc') {
    return <ChevronDown size={14} className="text-primary-600" />;
  }
  return null;
}

const SortableTableHeaderCell = ({
  header,
  enableColumnOrder = false,
}) => {
  const canSort = header.column.getCanSort();
  const canResize = header.column.getCanResize();
  const isResizing = header.column.getIsResizing();
  const sortHandler = canSort ? header.column.getToggleSortingHandler() : undefined;
  const filterVariant = header.column.columnDef.meta?.filterVariant;
  const showColumnFilter = header.column.getCanFilter()
    && (filterVariant === 'selectSingle'
      || filterVariant === 'selectMulti'
      || filterVariant === 'text'
      || filterVariant === 'date'
      || filterVariant === 'qtyRange');

  const locked = isColumnOrderLocked(header.column);
  const canReorder = enableColumnOrder && !locked;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.column.id,
    disabled: !canReorder,
  });

  const style = {
    width: header.getSize(),
    minWidth: getColumnMinWidth(header.column),
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <th
      ref={setNodeRef}
      className={cn(
        'relative',
        canSort && 'cursor-pointer shortcut_transition-colors hover:bg-primary-200',
        isResizing && 'th_resizing',
        isDragging && 'z-20 bg-primary-100 opacity-80 ring-2 ring-primary-300',
        header.column.columnDef.meta?.headerClassName,
      )}
      style={style}
      onClick={sortHandler}
    >
      <div className="flex flex-col items-center justify-center gap-1">
        <span className="inline-flex items-center justify-center gap-1">
          {canReorder ? (
            <button
              type="button"
              className="
                -ml-0.5 flex shrink-0 cursor-grab items-center text-gray-400
                hover:text-gray-600 active:cursor-grabbing
              "
              aria-label={`拖曳排序 ${header.column.id}`}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="size-3.5" aria-hidden />
            </button>
          ) : null}
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
          {renderSortIndicator(header.column.getIsSorted())}
        </span>
        {showColumnFilter ? (
          <div
            className="flex min-w-0 max-w-full justify-center"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            role="presentation"
          >
            {filterVariant === 'text' ? (
              <ColumnFilterTextInput column={header.column} />
            ) : filterVariant === 'date' ? (
              <ColumnFilterDateInput column={header.column} />
            ) : filterVariant === 'qtyRange' ? (
              <ColumnFilterQtyPopover column={header.column} />
            ) : (
              <ColumnFilterPopover column={header.column} />
            )}
          </div>
        ) : null}
      </div>
      {canResize ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="調整欄寬"
          className="th_resize_handle"
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => {
            event.stopPropagation();
            header.column.resetSize();
          }}
        />
      ) : null}
    </th>
  );
};

SortableTableHeaderCell.propTypes = {
  header: PropTypes.object.isRequired,
  enableColumnOrder: PropTypes.bool,
};

export default SortableTableHeaderCell;
